import { useCallback, useEffect, useRef } from 'react'
import { useAtom, useAtomValue } from 'jotai'
import {
    ticketOptionsAtom,
    isLoadingTicketOptionsAtom,
    ticketOptionsErrorAtom,
    hasLoadedTicketOptionsAtom
} from '@/core/store/ticket.store'
import { fetchTicketOptions } from '@/core/services/ticket.service'
import { RetryGuard } from '@repo/unicity/guard'

// Module-level flag to prevent double fetch across all instances
let isFetchingGlobal = false
// Guard rail: on error the catch resets hasFetchedRef so the effect re-fires — cap
// that to 3 consecutive failures so a persistently-failing endpoint stops hammering.
const retryGuard = new RetryGuard(3)

export const useTicketOptions = () => {
    const [tickets, setTickets] = useAtom(ticketOptionsAtom)
    const [isLoading, setIsLoading] = useAtom(isLoadingTicketOptionsAtom)
    const [error, setError] = useAtom(ticketOptionsErrorAtom)
    const [hasLoaded, setHasLoaded] = useAtom(hasLoadedTicketOptionsAtom)
    const hasFetchedRef = useRef(false)

    const fetchTickets = useCallback(async () => {
        if (hasLoaded || isLoading || isFetchingGlobal || hasFetchedRef.current || retryGuard.exhausted) return

        isFetchingGlobal = true
        hasFetchedRef.current = true
        setIsLoading(true)
        setError(null)

        try {
            const data = await fetchTicketOptions()
            setTickets(data)
            setHasLoaded(true)
            retryGuard.succeed()
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to fetch ticket options'
            setError(errorMessage)
            retryGuard.fail()
            // Allow another auto-attempt only while under the failure cap; once exhausted,
            // leave the fetched flag set so the effect stops re-firing.
            if (!retryGuard.exhausted) hasFetchedRef.current = false
        } finally {
            setIsLoading(false)
            isFetchingGlobal = false
        }
    }, [hasLoaded, isLoading, setIsLoading, setError, setTickets, setHasLoaded])

    useEffect(() => {
        if (!hasLoaded && !isLoading && !isFetchingGlobal) {
            fetchTickets()
        }
    }, [hasLoaded, isLoading, fetchTickets])

    return {
        tickets,
        isLoading,
        error,
        hasLoaded,
        fetchTickets
    }
}
